import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboardIcon, SmartphoneIcon, BarChart2Icon, PackageIcon, ShoppingBagIcon, RefreshCwIcon, ShieldIcon, ClipboardListIcon, UsersIcon, GavelIcon, MenuIcon, XIcon, UserIcon, LogOutIcon, BellIcon, SunIcon, MoonIcon, SearchIcon, StarIcon, PlusIcon, ChevronRightIcon, ActivityIcon, TrendingUpIcon, ClockIcon, InfoIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Card, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
interface LayoutProps {
  children: React.ReactNode;
}
interface NavItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  isActive: boolean;
  badge?: string;
  badgeVariant?: 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning';
}
interface QuickActionProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}
interface NotificationItemProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  time: string;
  isNew?: boolean;
}
const NavItem: React.FC<NavItemProps> = ({
  to,
  icon,
  label,
  isActive,
  badge,
  badgeVariant = 'default'
}) => {
  return <Link to={to} className={`flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${isActive ? 'bg-primary text-primary-foreground' : 'hover:bg-muted/80'}`} data-id="element-266">
      <span className="flex-shrink-0 w-5 h-5" data-id="element-267">{icon}</span>
      <span className="flex-grow font-medium" data-id="element-268">{label}</span>
      {badge && <Badge variant={badgeVariant} className="ml-auto" data-id="element-269">
          {badge}
        </Badge>}
    </Link>;
};
const NotificationItem: React.FC<NotificationItemProps> = ({
  icon,
  title,
  description,
  time,
  isNew = false
}) => <div className={`flex items-start p-3 ${isNew ? 'bg-primary/5' : ''} hover:bg-muted/50 rounded-md transition-colors`} data-id="element-270">
    <div className="flex-shrink-0 mr-3 mt-0.5" data-id="element-271">
      <div className="h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center" data-id="element-272">
        {icon}
      </div>
    </div>
    <div className="flex-1 min-w-0" data-id="element-273">
      <div className="flex items-center justify-between" data-id="element-274">
        <p className="font-medium truncate" data-id="element-275">{title}</p>
        {isNew && <Badge variant="success" className="ml-2" data-id="element-276">
            New
          </Badge>}
      </div>
      <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2" data-id="element-277">
        {description}
      </p>
      <p className="text-xs text-muted-foreground mt-1" data-id="element-278">{time}</p>
    </div>
  </div>;
const QuickAction: React.FC<QuickActionProps> = ({
  icon,
  label,
  onClick
}) => <button onClick={onClick} className="flex flex-col items-center justify-center bg-card hover:bg-muted/80 rounded-lg p-3 transition-colors" data-id="element-279">
    <div className="h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-2" data-id="element-280">
      {icon}
    </div>
    <span className="text-xs font-medium text-center" data-id="element-281">{label}</span>
  </button>;
const Layout: React.FC<LayoutProps> = ({
  children
}) => {
  const {
    pathname
  } = useLocation();
  const navigate = useNavigate();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const {
    theme,
    toggleTheme
  } = useTheme();
  const {
    user,
    logout
  } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [pinnedPages, setPinnedPages] = useState([{
    id: 1,
    to: '/market-analysis',
    label: 'Market Analysis',
    icon: <BarChart2Icon className="h-4 w-4" data-id="element-282" />
  }, {
    id: 2,
    to: '/inventory',
    label: 'Inventory',
    icon: <PackageIcon className="h-4 w-4" data-id="element-283" />
  }]);
  const notifications = [{
    id: 1,
    icon: <TrendingUpIcon className="h-4 w-4" data-id="element-284" />,
    title: 'iPhone 13 price alert',
    description: 'Price has increased by 5% in the last 24 hours. Consider adjusting your inventory pricing.',
    time: '10 minutes ago',
    isNew: true
  }, {
    id: 2,
    icon: <PackageIcon className="h-4 w-4" data-id="element-285" />,
    title: 'Low inventory alert',
    description: 'iPhone 12 Pro (128GB, Graphite) is running low. Only 3 units left in stock.',
    time: '1 hour ago',
    isNew: true
  }, {
    id: 3,
    icon: <ClockIcon className="h-4 w-4" data-id="element-286" />,
    title: 'Auction ending soon',
    description: 'The "Premium Samsung Collection" auction ends in 2 hours. Current bid: $12,450.',
    time: '2 hours ago',
    isNew: false
  }, {
    id: 4,
    icon: <ActivityIcon className="h-4 w-4" data-id="element-287" />,
    title: 'New market report available',
    description: 'July 2023 market analysis report is now available. View the latest trends and insights.',
    time: '1 day ago',
    isNew: false
  }];
  const recentSearches = ['iPhone 13 Pro Max pricing', 'Samsung S22 trade-in value', 'Pixel 6 inventory', 'Market trends Q3 2023'];
  const togglePinPage = (path, label, icon) => {
    const isPinned = pinnedPages.some(page => page.to === path);
    if (isPinned) {
      setPinnedPages(pinnedPages.filter(page => page.to !== path));
    } else {
      setPinnedPages([...pinnedPages, {
        id: Date.now(),
        to: path,
        label,
        icon
      }]);
    }
  };
  const navItems = [{
    to: '/',
    icon: <LayoutDashboardIcon className="h-5 w-5" data-id="element-288" />,
    label: 'Dashboard'
  }, {
    to: '/phone-grading',
    icon: <SmartphoneIcon className="h-5 w-5" data-id="element-289" />,
    label: 'AI Grading',
    badge: 'New',
    badgeVariant: 'success' as const
  }, {
    to: '/market-analysis',
    icon: <BarChart2Icon className="h-5 w-5" data-id="element-290" />,
    label: 'Market Analysis'
  }, {
    to: '/inventory',
    icon: <PackageIcon className="h-5 w-5" data-id="element-291" />,
    label: 'Inventory',
    badge: '162',
    badgeVariant: 'secondary' as const
  }, {
    to: '/deal-finder',
    icon: <ShoppingBagIcon className="h-5 w-5" data-id="element-292" />,
    label: 'Marketplace'
  }, {
    to: '/trade-in',
    icon: <RefreshCwIcon className="h-5 w-5" data-id="element-293" />,
    label: 'Trade-In',
    badge: '12',
    badgeVariant: 'warning' as const
  }, {
    to: '/risk-assessment',
    icon: <ShieldIcon className="h-5 w-5" data-id="element-294" />,
    label: 'Risk Assessment'
  }, {
    to: '/order-management',
    icon: <ClipboardListIcon className="h-5 w-5" data-id="element-295" />,
    label: 'Orders'
  }, {
    to: '/trader-crm',
    icon: <UsersIcon className="h-5 w-5" data-id="element-296" />,
    label: 'CRM'
  }, {
    to: '/wholesale-auctions',
    icon: <GavelIcon className="h-5 w-5" data-id="element-297" />,
    label: 'Auctions'
  }];
  const toggleMobileNav = () => {
    setMobileNavOpen(!mobileNavOpen);
  };
  const handleQuickAction = action => {
    switch (action) {
      case 'new-trade':
        navigate('/trade-in');
        break;
      case 'add-inventory':
        navigate('/inventory');
        break;
      case 'market-check':
        navigate('/market-analysis');
        break;
      case 'risk-check':
        navigate('/risk-assessment');
        break;
      default:
        break;
    }
  };
  useEffect(() => {
    setMobileNavOpen(false);
  }, [pathname]);
  const isPinned = path => pinnedPages.some(page => page.to === path);
  return <div className="flex h-screen bg-background" data-id="element-298">
      <aside className="hidden md:flex flex-col w-64 border-r bg-card" data-id="element-299">
        <div className="p-4 border-b" data-id="element-300">
          <Link to="/" className="flex items-center gap-2" data-id="element-301">
            <SmartphoneIcon className="h-6 w-6 text-primary" data-id="element-302" />
            <span className="font-bold text-xl" data-id="element-303">PhoneAI Market</span>
          </Link>
        </div>
        {pinnedPages.length > 0 && <div className="p-3 border-b" data-id="element-304">
            <div className="flex items-center justify-between mb-2" data-id="element-305">
              <span className="text-xs font-medium text-muted-foreground" data-id="element-306">
                PINNED PAGES
              </span>
            </div>
            <div className="space-y-1" data-id="element-307">
              {pinnedPages.map(page => <Link key={page.id} to={page.to} className={`flex items-center gap-2 px-3 py-1.5 rounded-md transition-colors ${pathname === page.to ? 'bg-primary text-primary-foreground' : 'hover:bg-muted/80'}`} data-id="element-308">
                  <span className="flex-shrink-0 w-4 h-4" data-id="element-309">{page.icon}</span>
                  <span className="flex-grow text-sm" data-id="element-310">{page.label}</span>
                  <StarIcon className="h-3.5 w-3.5 text-yellow-500 fill-yellow-500" onClick={e => {
              e.preventDefault();
              togglePinPage(page.to, page.label, page.icon);
            }} data-id="element-311" />
                </Link>)}
            </div>
          </div>}
        <nav className="flex-1 overflow-y-auto p-3 space-y-1" data-id="element-312">
          <div className="flex items-center justify-between mb-2" data-id="element-313">
            <span className="text-xs font-medium text-muted-foreground px-3" data-id="element-314">
              MAIN NAVIGATION
            </span>
          </div>
          {navItems.map(item => <div key={item.to} className="relative group" data-id="element-315">
              <NavItem to={item.to} icon={item.icon} label={item.label} isActive={pathname === item.to} badge={item.badge} badgeVariant={item.badgeVariant} data-id="element-316" />
              {!isPinned(item.to) && <button className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => togglePinPage(item.to, item.label, item.icon)} data-id="element-317">
                  <StarIcon className="h-3.5 w-3.5 text-muted-foreground hover:text-yellow-500" data-id="element-318" />
                </button>}
            </div>)}
        </nav>
        <div className="p-3 border-t" data-id="element-319">
          <div className="flex items-center justify-between mb-3" data-id="element-320">
            <TooltipProvider data-id="element-321">
              <Tooltip data-id="element-322">
                <TooltipTrigger asChild data-id="element-323">
                  <Button variant="ghost" size="icon" onClick={toggleTheme} className="h-8 w-8" data-id="element-324">
                    {theme === 'dark' ? <SunIcon className="h-4 w-4" data-id="element-325" /> : <MoonIcon className="h-4 w-4" data-id="element-326" />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent data-id="element-327">
                  <p data-id="element-328">Toggle {theme === 'dark' ? 'light' : 'dark'} mode</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Popover data-id="element-329">
              <PopoverTrigger asChild data-id="element-330">
                <Button variant="ghost" size="icon" className="h-8 w-8 relative" data-id="element-331">
                  <BellIcon className="h-4 w-4" data-id="element-332" />
                  <span className="absolute top-0 right-0 h-2 w-2 rounded-full bg-red-500" data-id="element-333"></span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-0" align="end" data-id="element-334">
                <div className="flex items-center justify-between p-3 border-b" data-id="element-335">
                  <span className="font-medium" data-id="element-336">Notifications</span>
                  <Badge variant="outline" data-id="element-337">2 new</Badge>
                </div>
                <ScrollArea className="h-80" data-id="element-338">
                  <div className="p-2 space-y-1" data-id="element-339">
                    {notifications.map(notification => <NotificationItem key={notification.id} icon={notification.icon} title={notification.title} description={notification.description} time={notification.time} isNew={notification.isNew} data-id="element-340" />)}
                  </div>
                </ScrollArea>
                <div className="p-2 border-t" data-id="element-341">
                  <Button variant="ghost" size="sm" className="w-full" data-id="element-342">
                    View all notifications
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
            <span className="text-xs text-muted-foreground" data-id="element-343">v1.2.4</span>
          </div>
          <DropdownMenu data-id="element-344">
            <DropdownMenuTrigger asChild data-id="element-345">
              <Button variant="ghost" className="w-full justify-start px-2 py-1.5" data-id="element-346">
                <div className="flex items-center gap-2" data-id="element-347">
                  <Avatar className="h-8 w-8" data-id="element-348">
                    <AvatarImage src="https://github.com/shadcn.png" data-id="element-349" />
                    <AvatarFallback data-id="element-350">AJ</AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col items-start text-sm" data-id="element-351">
                    <span className="font-medium" data-id="element-352">Alex Johnson</span>
                    <span className="text-xs text-muted-foreground" data-id="element-353">
                      Senior Trader
                    </span>
                  </div>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56" data-id="element-354">
              <Link to="/profile" data-id="element-355">
                <DropdownMenuItem data-id="element-356">
                  <UserIcon className="mr-2 h-4 w-4" data-id="element-357" />
                  <span data-id="element-358">Profile</span>
                </DropdownMenuItem>
              </Link>
              <DropdownMenuSeparator data-id="element-359" />
              <DropdownMenuItem onClick={logout} data-id="element-360">
                <LogOutIcon className="mr-2 h-4 w-4" data-id="element-361" />
                <span data-id="element-362">Log out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-background border-b" data-id="element-363">
        <div className="flex items-center justify-between p-4" data-id="element-364">
          <Link to="/" className="flex items-center gap-2" data-id="element-365">
            <SmartphoneIcon className="h-6 w-6 text-primary" data-id="element-366" />
            <span className="font-bold text-lg" data-id="element-367">PhoneAI Market</span>
          </Link>
          <div className="flex items-center gap-2" data-id="element-368">
            <Popover data-id="element-369">
              <PopoverTrigger asChild data-id="element-370">
                <Button variant="ghost" size="icon" className="relative" data-id="element-371">
                  <BellIcon className="h-5 w-5" data-id="element-372" />
                  <span className="absolute top-0 right-0 h-2 w-2 rounded-full bg-red-500" data-id="element-373"></span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-0" align="end" data-id="element-374">
                <div className="flex items-center justify-between p-3 border-b" data-id="element-375">
                  <span className="font-medium" data-id="element-376">Notifications</span>
                  <Badge variant="outline" data-id="element-377">2 new</Badge>
                </div>
                <ScrollArea className="h-80" data-id="element-378">
                  <div className="p-2 space-y-1" data-id="element-379">
                    {notifications.map(notification => <NotificationItem key={notification.id} icon={notification.icon} title={notification.title} description={notification.description} time={notification.time} isNew={notification.isNew} data-id="element-380" />)}
                  </div>
                </ScrollArea>
                <div className="p-2 border-t" data-id="element-381">
                  <Button variant="ghost" size="sm" className="w-full" data-id="element-382">
                    View all notifications
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
            <Button variant="ghost" size="icon" onClick={toggleMobileNav} data-id="element-383">
              {mobileNavOpen ? <XIcon className="h-5 w-5" data-id="element-384" /> : <MenuIcon className="h-5 w-5" data-id="element-385" />}
            </Button>
          </div>
        </div>
      </div>
      {mobileNavOpen && <div className="md:hidden fixed inset-0 bg-black/50 z-40" onClick={toggleMobileNav} data-id="element-386" />}
      <aside className={`md:hidden fixed top-[57px] left-0 bottom-0 w-64 bg-card z-50 transform transition-transform duration-300 ease-in-out ${mobileNavOpen ? 'translate-x-0' : '-translate-x-full'}`} data-id="element-387">
        <div className="p-3 border-b" data-id="element-388">
          <div className="relative" data-id="element-389">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" data-id="element-390" />
            <input type="text" placeholder="Search..." className="w-full pl-10 pr-4 py-2 rounded-md border bg-background text-sm" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-391" />
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto p-3 space-y-1" data-id="element-392">
          {navItems.map(item => <NavItem key={item.to} to={item.to} icon={item.icon} label={item.label} isActive={pathname === item.to} badge={item.badge} badgeVariant={item.badgeVariant} data-id="element-393" />)}
        </nav>
        <div className="p-3 border-t" data-id="element-394">
          <div className="flex items-center justify-between mb-3" data-id="element-395">
            <Button variant="ghost" size="icon" onClick={toggleTheme} className="h-8 w-8" data-id="element-396">
              {theme === 'dark' ? <SunIcon className="h-4 w-4" data-id="element-397" /> : <MoonIcon className="h-4 w-4" data-id="element-398" />}
            </Button>
            <span className="text-xs text-muted-foreground" data-id="element-399">v1.2.4</span>
          </div>
          <Link to="/profile" data-id="element-400">
            <Button variant="ghost" className="w-full justify-start px-2 py-1.5" data-id="element-401">
              <div className="flex items-center gap-2" data-id="element-402">
                <Avatar className="h-8 w-8" data-id="element-403">
                  <AvatarImage src="https://github.com/shadcn.png" data-id="element-404" />
                  <AvatarFallback data-id="element-405">AJ</AvatarFallback>
                </Avatar>
                <div className="flex flex-col items-start text-sm" data-id="element-406">
                  <span className="font-medium" data-id="element-407">Alex Johnson</span>
                  <span className="text-xs text-muted-foreground" data-id="element-408">
                    Senior Trader
                  </span>
                </div>
              </div>
            </Button>
          </Link>
        </div>
      </aside>
      <main className="flex-1 flex flex-col h-screen overflow-hidden" data-id="element-409">
        <div className="flex-1 overflow-y-auto p-4 md:p-6 pt-[calc(57px+1rem)] md:pt-6" data-id="element-410">
          {pathname === '/' && <Card className="mb-6 bg-primary/5 border-primary/20" data-id="element-411">
              <CardContent className="p-4" data-id="element-412">
                <div className="flex items-center justify-between" data-id="element-413">
                  <div className="flex items-center gap-3" data-id="element-414">
                    <div className="h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center" data-id="element-415">
                      <ActivityIcon className="h-5 w-5" data-id="element-416" />
                    </div>
                    <div data-id="element-417">
                      <h3 className="font-medium" data-id="element-418">Daily Market Insight</h3>
                      <p className="text-sm text-muted-foreground" data-id="element-419">
                        iPhone 13 prices have stabilized after 3 weeks of
                        decline. Good time to restock inventory.
                      </p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" className="whitespace-nowrap" data-id="element-420">
                    View Details
                    <ChevronRightIcon className="h-4 w-4 ml-1" data-id="element-421" />
                  </Button>
                </div>
              </CardContent>
            </Card>}
          {children}
        </div>
      </main>
    </div>;
};
export default Layout;